import React, { useEffect, useState } from "react";
import { Globe, Radio } from "lucide-react";
import { mitSitzungspruefung } from "../utils/fetchWithAuth";

export default function Freischalten() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("token");

  const [pubState, setPubState] = useState({
    results_published: false,
    published_at: null,
    live_team_view: false,
  });
  const [loadingPub, setLoadingPub] = useState(false);
  const [errorPub, setErrorPub] = useState(null);
  const [loadingLive, setLoadingLive] = useState(false);
  const [errorLive, setErrorLive] = useState(null);

  // === Öffentliche Freigabe laden ===
  useEffect(() => {
    const loadState = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/public/state`);
        if (!res.ok) throw new Error("Fehler beim Laden des Freigabe-Status");
        const data = await res.json();
        setPubState(data);
      } catch (err) {
        console.error(err);
      }
    };
    loadState();
  }, [BACKEND_URL]);

  // === Freigabe / Sperre ===
  const togglePublish = async (published) => {
    // Der folgenreichste Schalter der Anwendung: danach sind Gesamtwertung,
    // Plaetze und Punkte aller Teams fuer jeden abrufbar – auch ueber die
    // Team-Links. Ein Fehlklick waehrend der Veranstaltung nimmt die
    // Siegerehrung vorweg, deshalb nicht ohne Rueckfrage. Das Zuruecknehmen
    // ist harmlos und bleibt ein Klick.
    if (
      published &&
      !window.confirm(
        "Ergebnisse jetzt öffentlich freigeben?\n\n" +
          "Danach kann JEDER die komplette Rangliste mit Plätzen und Punkten sehen – " +
          "auch über die Team-Links. Das nimmt die Siegerehrung vorweg.\n\n" +
          "Normalerweise erst NACH der Siegerehrung freigeben."
      )
    ) {
      return;
    }
    setLoadingPub(true);
    setErrorPub(null);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/admin/publish-results`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ published }),
      }));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Fehler beim Umschalten");
      setPubState((bisher) => ({ ...bisher, ...data.state }));
    } catch (err) {
      console.error(err);
      setErrorPub(err.message || "Fehler beim Umschalten");
    } finally {
      setLoadingPub(false);
    }
  };

  // === Live-Team-Ansicht umschalten ===
  //
  // Eigener Schalter, nicht Teil der Ergebnis-Freigabe: die Team-Links laufen
  // WAEHREND der Veranstaltung, die Gesamtwertung wird erst DANACH freigegeben.
  const toggleLiveTeamView = async (enabled) => {
    setLoadingLive(true);
    setErrorLive(null);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/admin/live-team-view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled }),
      }));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Fehler beim Umschalten");
      setPubState((bisher) => ({ ...bisher, ...data.state }));
    } catch (err) {
      console.error(err);
      setErrorLive(err.message || "Fehler beim Umschalten");
    } finally {
      setLoadingLive(false);
    }
  };

  return (
    <div className="p-6 bg-secondary-950 text-white min-h-screen">
      <h1 className="text-2xl font-bold mb-6">Freischalten</h1>

      {/* === Öffentliche Freigabe === */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4 flex flex-col items-start mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Globe className="text-blue-400" />
          <h2 className="font-semibold text-lg">Öffentliche Ergebnisse</h2>
        </div>

        <p className="text-sm text-gray-400 mb-3">
          {pubState.results_published
            ? "✅ Ergebnisse sind aktuell öffentlich sichtbar."
            : "⛔ Ergebnisse sind aktuell NICHT öffentlich sichtbar."}
          {pubState.published_at && (
            <span className="block mt-1 text-xs text-gray-500">
              Freigegeben am:{" "}
              {new Date(pubState.published_at).toLocaleString("de-DE")}
            </span>
          )}
        </p>

        <div className="flex flex-wrap gap-2 w-full">
          <button
            disabled={loadingPub}
            onClick={() => togglePublish(true)}
            className="flex-1 px-4 py-2 rounded bg-green-500 hover:bg-green-600 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            Ergebnisse freigeben
          </button>
          <button
            disabled={loadingPub}
            onClick={() => togglePublish(false)}
            className="flex-1 px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            Freigabe aufheben
          </button>
        </div>

        {errorPub && (
          <p className="text-red-400 text-sm mt-3">❌ {errorPub}</p>
        )}
      </div>

      {/* === Live-Ansicht für die Teams === */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4 flex flex-col items-start mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Radio className="text-green-400" />
          <h2 className="font-semibold text-lg">Live-Ansicht für Teams</h2>
        </div>

        <p className="text-sm text-gray-400 mb-1">
          {pubState.live_team_view
            ? "✅ Teams können ihre eigenen Ergebnisse über ihren Link abrufen."
            : "⛔ Die Team-Links sind aktuell gesperrt."}
        </p>
        <p className="text-xs text-gray-500 mb-3">
          Jedes Team sieht dabei ausschließlich seine eigenen Stationsergebnisse –
          ohne Platzierung und ohne andere Teams. Den persönlichen Link findest du
          in den Team-Details.
        </p>

        <div className="flex flex-wrap gap-2 w-full">
          <button
            disabled={loadingLive}
            onClick={() => toggleLiveTeamView(true)}
            className="flex-1 px-4 py-2 rounded bg-green-500 hover:bg-green-600 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            Live-Ansicht freischalten
          </button>
          <button
            disabled={loadingLive}
            onClick={() => toggleLiveTeamView(false)}
            className="flex-1 px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            Live-Ansicht sperren
          </button>
        </div>

        {errorLive && (
          <p className="text-red-400 text-sm mt-3">❌ {errorLive}</p>
        )}
      </div>
    </div>
  );
}
